import {
  alpha,
  Box,
  Button,
  IconButton,
  Link,
  ListItem,
  Tooltip,
  useTheme,
} from '@mui/material'
import { memo, ReactNode, useMemo } from 'react'
import { NavLink, useMatch } from 'react-router-dom'

import { TitleBox } from '@/components'
import { useHomeQuery } from '@/services/api'
import { Alert } from '@/types'

function SidebarItem(props: SidebarItemProps) {
  const { icon, title, chip, href, token, collapsed, alerts } = props
  const matches = useMatch({ path: href, end: href === '/' ? true : false })
  const isActive = Boolean(matches)
  const { data } = useHomeQuery()
  const theme = useTheme()

  const unreadAlertsCount = alerts.filter(({ read_at }) => !read_at).length

  const getProps = useMemo(() => {
    if (href.startsWith('http')) {
      return {
        component: Link,
        href: title === 'Minha Biblioteca' ? href + `/?token=${token}` : href,
        target: '_blank',
      }
    }

    return {
      component: NavLink,
      to: href,
    }
  }, [href, title, token])

  const getCount = useMemo(() => {
    if (title === 'Mensagens') return data?.unread_messages_count || 0
    if (title === 'Comunicados') return data?.unread_alerts_count || 0
    return 0
  }, [title, data])

  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: isActive ? 'primary.main' : 'text.secondary',
        transition: theme.transitions.create(['color'], {
          duration: theme.transitions.duration.short,
        }),
      }}>
      {icon}
    </Box>
  )

  if (collapsed) {
    return (
      <ListItem disableGutters sx={{ display: 'flex', py: 0.5, px: 0 }}>
        <Tooltip title={title} placement="right">
          <IconButton
            {...getProps}
            sx={{
              width: '100%',
              height: 48,
              borderRadius: 2,
              backgroundColor: isActive
                ? alpha(theme.palette.primary.main, 0.12)
                : 'transparent',
              color: isActive ? 'primary.main' : 'text.secondary',
              border: 1,
              borderColor: isActive
                ? alpha(theme.palette.primary.main, 0.2)
                : 'transparent',
              transition: theme.transitions.create(['all'], {
                duration: theme.transitions.duration.short,
              }),
              position: 'relative',
              '&:hover': {
                backgroundColor: isActive
                  ? alpha(theme.palette.primary.main, 0.16)
                  : alpha(theme.palette.action.hover, 0.08),
                borderColor: alpha(theme.palette.primary.main, 0.2),
                transform: 'scale(1.02)',
                boxShadow: theme.shadows[2],
              },
            }}>
            {content}
            {getCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  minWidth: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: theme.shadows[2],
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </ListItem>
    )
  }

  return (
    <ListItem
      disableGutters
      sx={{
        display: 'flex',
        py: 0.5,
        px: 0,
      }}>
      <Button
        {...getProps}
        startIcon={content}
        variant="text"
        fullWidth
        sx={{
          color: isActive ? 'text.primary' : 'text.secondary',
          backgroundColor: isActive
            ? alpha(theme.palette.primary.main, 0.08)
            : 'transparent',
          fontWeight: isActive ? 600 : 500,
          justifyContent: 'flex-start',
          textAlign: 'left',
          px: 2,
          py: 1.5,
          textTransform: 'none',
          borderRadius: 2,
          border: 1,
          borderColor: isActive ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
          transition: theme.transitions.create(['all'], {
            duration: theme.transitions.duration.short,
          }),
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: isActive
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.action.hover, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.2),
            transform: 'translateX(4px)',
            boxShadow: theme.shadows[2],
          },
        }}>
        {title !== 'Comunicados' && (
          <TitleBox
            title={title}
            chip={chip}
            count={data?.unread_messages_count || 0}
            shouldCount={title === 'Mensagens'}
          />
        )}
        {title === 'Comunicados' && (
          <TitleBox
            title={title}
            chip={chip}
            count={unreadAlertsCount || 0}
            shouldCount={title === 'Comunicados'}
          />
        )}
      </Button>
    </ListItem>
  )
}

type SidebarItemProps = {
  icon?: ReactNode
  title: string
  href: string
  token: string
  collapsed?: boolean
  alerts: Alert[]
  chip?: {
    text: string
    flag?: string
  }
}

export default memo(SidebarItem)
